import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';  // Importer useNavigate pour la navigation

const pointsPerQuizAnswer = 50;
const maxPoints = 1000;

// Fonction pour calculer la progression basée sur le nombre total de points
export const calculateProgress = (competenceProgress, competence) => {
  if (!competenceProgress || !competenceProgress[competence]) {
    console.warn(`Aucune donnée trouvée pour la compétence : ${competence}. Retour à 0% de progression.`);
    return 0;
  }

  const { totalCorrectAnswers = 0, Points_Exercices = 0 } = competenceProgress[competence];
  const quizPoints = totalCorrectAnswers * pointsPerQuizAnswer;
  const totalPoints = quizPoints + Points_Exercices;
  const progressPercentage = Math.min((totalPoints / maxPoints) * 100, 100);

  console.log(`Calcul de la progression pour ${competence} :`);
  console.log(`  - Points Quiz : ${quizPoints}`);
  console.log(`  - Points Exercices : ${Points_Exercices}`);
  console.log(`  - Total Points : ${totalPoints}`);
  console.log(`  - Progression calculée : ${progressPercentage}%`);

  return Math.round(progressPercentage);
};

// Fonction pour déterminer le niveau de compétence en fonction de la progression
export const getLevel = (progress) => {
  console.log("Détermination du niveau de compétence...");
  if (progress <= 25) return 'Débutant';
  if (progress <= 50) return 'En progrès';
  if (progress <= 75) return 'Avancé';
  return 'Maitrisé';
};

// Initialisation de la progression pour une compétence spécifique
export const initializeCompetenceProgress = (competenceProgress, competenceId) => {
  console.log("Initialisation de la progression pour une compétence...");
  const initialProgress = competenceProgress[competenceId] || {
    quizCompleted: 0,
    exercisesCompleted: 0,
    Points_Exercices: 0,
    totalCorrectAnswers: 0,
    totalQuestionsAnswered: 0,
    progress: 0,
    courseListened: false,
  };
  console.log(`Initialisation de la progression pour ${competenceId} :`, JSON.stringify(initialProgress));
  return initialProgress;
};

// Initialisation de la progression pour toutes les compétences en utilisant le champ `id` de chaque compétence comme clé
export const initializeAllCompetencesProgress = (competenceProgress, competenceData) => {
  competenceData.forEach(({ id: competenceId }) => {
    if (!competenceProgress[competenceId]) {
      competenceProgress[competenceId] = initializeCompetenceProgress(competenceProgress, competenceId);
    }
  });
  return competenceProgress;
};

// Charger la progression d'un utilisateur pour ses compétences depuis Firestore
export const loadUserProgress = async (userId) => {
  console.log("Chargement de la progression utilisateur... UserID reçu:", userId, "Type:", typeof userId);
  
  if (!userId || typeof userId !== 'string') {
    console.error("userId est indéfini ou non valide (pas une chaîne). Impossible de charger la progression de l'utilisateur.");
    return {};
  }

  const userProgressRef = doc(db, "users", userId);
  try {
    const userProgressSnap = await getDoc(userProgressRef);
    const competencesCollection = collection(db, "users", userId, "competences");
    const competencesSnapshot = await getDocs(competencesCollection);
    const competenceData = competencesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    if (userProgressSnap.exists()) {
      let competenceProgress = userProgressSnap.data().competenceProgress || {};

      competenceProgress = initializeAllCompetencesProgress(competenceProgress, competenceData);

      await setDoc(userProgressRef, { competenceProgress }, { merge: true });

      return competenceProgress;
    } else {
      const initializedCompetenceProgress = initializeAllCompetencesProgress({}, competenceData);
      await setDoc(userProgressRef, { competenceProgress: initializedCompetenceProgress });
      return initializedCompetenceProgress;
    }
  } catch (error) {
    console.error("Erreur lors de la récupération de la progression:", error.message);
    throw new Error("Erreur lors de la récupération des données de progression.");
  }
};

// Fonction pour sauvegarder la progression dans Firestore
export const saveProgressToFirestore = async (userId, competenceId, updatedProgress) => {
  console.log("Début de la sauvegarde de la progression dans Firestore... paramètres :", { userId, competenceId, updatedProgress });

  if (!userId || typeof userId !== 'string') {
    console.error("Erreur : userId est indéfini ou n'est pas une chaîne.");
    return;
  }

  if (!competenceId || typeof competenceId !== 'string') {
    console.error("Erreur : competenceId est indéfini ou n'est pas une chaîne.");
    return;
  }

  const userProgressRef = doc(db, "users", userId);
  try {
    const currentData = await loadUserProgress(userId);
    const competenceData = currentData[competenceId] || initializeCompetenceProgress(currentData, competenceId);

    const newCompetenceProgress = {
      ...currentData,
      [competenceId]: {
        ...competenceData,
        ...updatedProgress,
      }
    };

    await setDoc(userProgressRef, {
      competenceProgress: newCompetenceProgress
    }, { merge: true });

    console.log(`Progression sauvegardée avec succès pour la compétence ${competenceId}.`);
  } catch (error) {
    console.error("Erreur lors de la sauvegarde:", error.message);
    throw new Error("Erreur lors de la sauvegarde de la progression.");
  }
};

// Mise à jour de la progression après une réponse au quiz
export const updateQuizProgress = async (user, competenceId, totalCorrectAnswers, totalQuestionsAnswered) => {
  console.log("Mise à jour de la progression après une réponse au quiz...");

  const updatedProgress = {
    totalCorrectAnswers,
    totalQuestionsAnswered,
  };

  const competenceProgress = await loadUserProgress(user);
  const newProgress = calculateProgress(competenceProgress, competenceId);

  console.log(`Nouveau calcul de progression pour ${competenceId}: ${newProgress}%`);

  await saveProgressToFirestore(user, competenceId, { ...updatedProgress, progress: newProgress });

  console.log(`Progression mise à jour pour ${competenceId}: ${newProgress} (Total Correct Answers: ${totalCorrectAnswers}, Total Questions Answered: ${totalQuestionsAnswered})`);
};

// Mise à jour de la progression après la complétion d'un exercice
export const updateExerciseProgress = async (user, competenceId, exercisesCompleted, pointsEarned) => {

  const competenceProgress = await loadUserProgress(user);
  const currentPoints = competenceProgress[competenceId]?.Points_Exercices || 0;

  const updatedProgress = {
    exercisesCompleted,
    Points_Exercices: currentPoints + pointsEarned,
  };

  console.log(`Points avant mise à jour pour ${competenceId} : ${currentPoints}`);
  console.log(`Points ajoutés pour l'exercice : ${pointsEarned}`);

  const newProgress = calculateProgress(competenceProgress, competenceId);

  await saveProgressToFirestore(user, competenceId, { ...updatedProgress, progress: newProgress });

  console.log(`Exercices terminés pour ${competenceId}: progression mise à jour avec ${updatedProgress.Points_Exercices} points cumulés pour les exercices.`);
};
